import { z } from 'zod';
import { dayBoundsInTz, localDateString, localWeekday } from './timezones.js';

/**
 * Horário de funcionamento do lote (#393, #396).
 *
 * Um horário por dia da semana, mais um para feriado. Cada dia é "24 horas",
 * "fechado" ou uma faixa de abertura e fechamento no relógio do pátio. Lote sem
 * configuração funciona 24h todos os dias — é o que todo lote de antes é.
 *
 * O horário não muda o preço: carro que fica depois do fechamento segue com o
 * relógio correndo, e se a estadia entra na noite vale o pernoite (decisão do
 * dono na #393). Ele serve para avisar o cliente e o operador e para bloquear
 * ticket novo com o pátio fechado.
 */

const MINUTES_IN_DAY = 24 * 60;
const minuteOfDay = z.number().int().min(0).max(MINUTES_IN_DAY);

export const dayHoursSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('24h') }),
  z.object({ kind: z.literal('closed') }),
  z
    .object({
      kind: z.literal('hours'),
      /** Minutos desde a meia-noite local: 450 é 07:30. */
      open: minuteOfDay,
      close: minuteOfDay,
    })
    // Faixa que atravessa a meia-noite fica para depois: nenhuma placa da #396
    // tem, e ela muda o que "o dia" quer dizer para o aviso de fechamento.
    .refine((d) => d.close > d.open, 'o fechamento precisa ser depois da abertura'),
]);
export type DayHours = z.infer<typeof dayHoursSchema>;

/** No feriado, "igual ao dia da semana" é o padrão: só muda se o dono disser. */
export const holidayHoursSchema = z.union([
  dayHoursSchema,
  z.object({ kind: z.literal('weekday') }),
]);
export type HolidayHours = z.infer<typeof holidayHoursSchema>;

export const operatingHoursSchema = z.object({
  /** Domingo a sábado, na ordem de `Date#getDay`. */
  weekdays: z.array(dayHoursSchema).length(7),
  holiday: holidayHoursSchema.default({ kind: 'weekday' }),
  /** Abrir ticket com o pátio fechado. Desligado, a abertura é recusada. */
  allowTicketsWhenClosed: z.boolean().default(false),
  /**
   * Antecedência do aviso vermelho de fechamento, para cliente e operador. É
   * também a hora do SMS e do e-mail.
   */
  closingWarningMinutes: z.number().int().min(5).max(180).default(30),
  /**
   * Antecedência do aviso amarelo no link do ticket (#486): o cliente fica
   * sabendo cedo, sem custo de SMS. Abaixo do vermelho, só o vermelho aparece.
   */
  closingHeadsUpMinutes: z.number().int().min(15).max(360).default(120),
  /**
   * Avisar também por SMS e por e-mail, na mesma antecedência, quem deixou
   * celular ou e-mail no ticket. Desligado por padrão: o link do ticket já
   * avisa, e cada SMS entra no consumo do módulo (#393).
   */
  closingNoticeSms: z.boolean().default(false),
  closingNoticeEmail: z.boolean().default(false),
});
export type OperatingHours = z.infer<typeof operatingHoursSchema>;

export const ALWAYS_OPEN: OperatingHours = {
  weekdays: Array.from({ length: 7 }, () => ({ kind: '24h' as const })),
  holiday: { kind: 'weekday' },
  allowTicketsWhenClosed: false,
  closingWarningMinutes: 30,
  closingHeadsUpMinutes: 120,
  closingNoticeSms: false,
  closingNoticeEmail: false,
};

/** O feriado só importa se tiver horário próprio. */
export const hoursUseHolidays = (hours: OperatingHours | null | undefined): boolean =>
  Boolean(hours && hours.holiday.kind !== 'weekday');

export interface OpeningStatus {
  isOpen: boolean;
  /** Quando fecha hoje, se está aberto e não é 24h. */
  closesAt: Date | null;
  /** Quando abre de novo, se está fechado. Nulo se não abre nos próximos 8 dias. */
  opensAt: Date | null;
  /** Falta menos que a antecedência do aviso vermelho para fechar. */
  closingSoon: boolean;
  /** Falta menos que a antecedência do aviso amarelo para fechar (#486). */
  closingHeadsUp: boolean;
}

const MINUTE_MS = 60_000;

const hoursFor = (
  ms: number,
  hours: OperatingHours,
  timezone: string,
  holidays: ReadonlySet<string>,
) => {
  const at = new Date(ms);
  const weekday = hours.weekdays[localWeekday(at, timezone)] ?? { kind: '24h' as const };
  if (holidays.has(localDateString(at, timezone)) && hours.holiday.kind !== 'weekday') {
    return hours.holiday;
  }
  return weekday;
};

/** Primeira abertura a partir do dia seguinte ao de `fromMs`, até 8 dias. */
const nextOpening = (
  fromMs: number,
  hours: OperatingHours,
  timezone: string,
  holidays: ReadonlySet<string>,
): Date | null => {
  let dayStart = dayBoundsInTz(new Date(fromMs), timezone).endMs;
  for (let i = 0; i < 8; i += 1) {
    const day = hoursFor(dayStart, hours, timezone, holidays);
    if (day.kind === '24h') return new Date(dayStart);
    if (day.kind === 'hours') return new Date(dayStart + day.open * MINUTE_MS);
    // 25h depois cai com folga no dia seguinte, mesmo com mudança de offset.
    dayStart = dayBoundsInTz(new Date(dayStart + 25 * 60 * MINUTE_MS), timezone).startMs;
  }
  return null;
};

/**
 * Aberto ou fechado em `at`, no relógio do lote. Sem configuração é sempre
 * aberto, sem horário de fechamento.
 */
export const openingStatus = (
  at: Date,
  hours: OperatingHours | null | undefined,
  timezone: string,
  holidays: readonly string[] = [],
): OpeningStatus => {
  const open24: OpeningStatus = {
    isOpen: true,
    closesAt: null,
    opensAt: null,
    closingSoon: false,
    closingHeadsUp: false,
  };
  if (!hours) return open24;
  const holidaySet = new Set(holidays);
  const ms = at.getTime();
  const day = hoursFor(ms, hours, timezone, holidaySet);
  if (day.kind === '24h') return open24;

  const dayStart = dayBoundsInTz(at, timezone).startMs;
  if (day.kind === 'hours') {
    const openMs = dayStart + day.open * MINUTE_MS;
    const closeMs = dayStart + day.close * MINUTE_MS;
    if (ms >= openMs && ms < closeMs) {
      return {
        isOpen: true,
        closesAt: new Date(closeMs),
        opensAt: null,
        closingSoon: closeMs - ms <= hours.closingWarningMinutes * MINUTE_MS,
        closingHeadsUp: closeMs - ms <= hours.closingHeadsUpMinutes * MINUTE_MS,
      };
    }
    if (ms < openMs) {
      return {
        isOpen: false,
        closesAt: null,
        opensAt: new Date(openMs),
        closingSoon: false,
        closingHeadsUp: false,
      };
    }
  }
  return {
    isOpen: false,
    closesAt: null,
    opensAt: nextOpening(ms, hours, timezone, holidaySet),
    closingSoon: false,
    closingHeadsUp: false,
  };
};

/** `450` → `07:30`. */
export const formatMinuteOfDay = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** `07:30` → `450`; `null` se o texto não for uma hora válida. */
export const parseMinuteOfDay = (value: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return Number(match[2]) < 60 && minutes <= MINUTES_IN_DAY ? minutes : null;
};
